import React, { createContext, useState, useEffect, useCallback } from 'react';
import { ELanguages } from '../../types/enums/ELanguages';
import { ESessionStorageKeys } from '../../types/enums/ESessionStorageKeys';
import { useCallbackOne } from 'use-memo-one';
import { config } from '../../constants/config';
import { ISystemUserPermissions } from '../../types/interfaces/ISystemUserPermissions';

const systemUserDefaultPermissions: ISystemUserPermissions = {
  packages: {
    view: false,
    edit: false,
  },
  customers: {
    view: false,
    edit: false,
  },
  systemUsers: {
    view: false,
    edit: false,
  },
  promoCampaigns: {
    view: false,
    edit: false,
  },
  carrierStatus: {
    view: false,
    edit: false,
  },
  notifications: {
    view: false,
    edit: false,
  },
  appSettings: {
    view: false,
    edit: false,
  },
  promocodes: {
    view: false,
    edit: false,
  },
  // TODO: uncomment in MVP 2
  // calculator: {
  //   view: false,
  //   edit: false,
  // },
};

export interface IGlobalValues {
  language: ELanguages;
  setLanguage: React.Dispatch<React.SetStateAction<ELanguages>>;
  token?: string;
  updateToken: (token?: string) => void;
  showSessionEnd: boolean;
  setShowSessionEnd: React.Dispatch<React.SetStateAction<boolean>>;
  resetSessionTimer: () => void;
  isTokenRead: boolean;
  setPermissions: (permissions: ISystemUserPermissions) => void;
  permissions: ISystemUserPermissions;
  permissionsLoaded: boolean;
}
const initialGlobalValues: IGlobalValues = {
  language: ELanguages.English,
  setLanguage: () => null,
  updateToken: () => null,
  showSessionEnd: false,
  setShowSessionEnd: () => null,
  resetSessionTimer: () => null,
  isTokenRead: false,
  setPermissions: () => null,
  permissions: systemUserDefaultPermissions,
  permissionsLoaded: false,
};

export const SessionContext = createContext<IGlobalValues>(initialGlobalValues);

let sessionTimer: NodeJS.Timeout | undefined = undefined;

const clearSessionTimer = () => {
  if (sessionTimer) {
    clearTimeout(sessionTimer);
  }
};

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguage] = useState(initialGlobalValues.language);
  const [token, setToken] = useState<string | undefined>(initialGlobalValues.token);
  const [showSessionEnd, setShowSessionEnd] = useState(initialGlobalValues.showSessionEnd);
  const [isTokenRead, setIsTokenRead] = useState(initialGlobalValues.isTokenRead);
  const [permissions, setPermissions] = useState<ISystemUserPermissions>(initialGlobalValues.permissions);
  const [permissionsLoaded, setPermissionsLoaded] = useState(initialGlobalValues.permissionsLoaded);

  const handleTokenFromSessionStorage = useCallback(() => {
    const storageToken = sessionStorage.getItem(ESessionStorageKeys.Token);
    if (storageToken) {
      setToken(storageToken);
    }
    setIsTokenRead(true);
  }, []);

  useEffect(() => {
    handleTokenFromSessionStorage();
  }, [handleTokenFromSessionStorage]);

  const openSessionEndDialog = useCallbackOne(() => {
    if (token) {
      setShowSessionEnd(true);
    }
  }, [token]);

  const updateToken = useCallbackOne((token?: string) => {
    if (token) {
      sessionStorage.setItem(ESessionStorageKeys.Token, token);
    } else {
      sessionStorage.removeItem(ESessionStorageKeys.Token);
    }
    setToken(token);
  }, []);

  const resetSessionTimer = useCallbackOne(() => {
    clearSessionTimer();
    sessionTimer = setTimeout(openSessionEndDialog, config.sessionEndMessageTimeout);
  }, [clearSessionTimer, openSessionEndDialog, token]);

  useEffect(() => {
    if (!token) {
      clearSessionTimer();
    }
  }, [token]);

  const handleSetPermissions = useCallback((permissions) => {
    setPermissions(permissions);
    setPermissionsLoaded(true);
  }, []);

  return (
    <SessionContext.Provider
      value={{
        language,
        setLanguage,
        token,
        updateToken,
        showSessionEnd,
        setShowSessionEnd,
        resetSessionTimer,
        isTokenRead,
        permissions,
        setPermissions: handleSetPermissions,
        permissionsLoaded,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
